/**
 * HR Commission Packages — Phase 1C catalog with tier definitions.
 *
 * Tier validation is light: the calc engine just walks tier_order; UI
 * sanity-checks min_amount ordering and at least one tier with rate.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useCommissionPackages,
  useUpsertCommissionPackage,
  useDeleteCommissionPackage,
  type CommissionPackageRow,
  type CommissionTierRow,
} from '../../hooks';
import { Plus, Pencil, Trash2, X, Save } from 'lucide-react';
import { COMMISSION_BASES } from '@payroll/shared';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

const BASIS_LABEL: Record<string, string> = {
  gross_sales: 'ยอดขายรวม',
  units_sold: 'จำนวนชิ้นที่ขายได้',
  gross_profit: 'กำไรขั้นต้น',
};

type Editing = Partial<Omit<CommissionPackageRow, 'tiers'>> & {
  tiers: Array<Partial<CommissionTierRow>>;
};

const EMPTY: Editing = {
  name: '',
  description: '',
  basis: 'gross_sales',
  min_threshold: 0,
  cap: null,
  floor: null,
  active: true,
  tiers: [{ tier_order: 1, min_amount: 0, max_amount: null, rate_percent: 0, rate_fixed: null }],
};

/** Content-only version used by the Commission hub. */
export function HRCommissionPackagesContent() {
  const { data: pkgs, isLoading } = useCommissionPackages();
  const upsert = useUpsertCommissionPackage();
  const remove = useDeleteCommissionPackage();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Editing | null>(null);

  async function handleSave() {
    if (!editing || !editing.name) {
      toast.error('กรอกชื่อ package');
      return;
    }
    if (!editing.tiers || editing.tiers.length === 0) {
      toast.error('ต้องมีอย่างน้อย 1 ขั้น');
      return;
    }
    try {
      await upsert.mutateAsync(editing as Partial<CommissionPackageRow> & { tiers: CommissionTierRow[] });
      setEditing(null);
    } catch (e) {
      toast.error(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  function addTier() {
    if (!editing) return;
    const next = (editing.tiers ?? []).length + 1;
    const lastMax = editing.tiers?.[next - 2]?.max_amount ?? 0;
    setEditing({
      ...editing,
      tiers: [...(editing.tiers ?? []), { tier_order: next, min_amount: lastMax ?? 0, max_amount: null, rate_percent: 0, rate_fixed: null }],
    });
  }
  function removeTier(idx: number) {
    if (!editing) return;
    setEditing({
      ...editing,
      tiers: (editing.tiers ?? [])
        .filter((_, i) => i !== idx)
        .map((t, i) => ({ ...t, tier_order: i + 1 })),
    });
  }

  return (
    <>
      {/* Action bar — replaces AdminLayout `actions` slot when rendered inside the
          Commission hub (the hub's header has no per-tab action area). */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600">กำหนดอัตราขั้นบันได + เพดาน + ขั้นต่ำรับประกัน</p>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /> เพิ่ม
        </button>
      </div>
      {isLoading ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {(pkgs ?? []).map((p) => (
            <div key={p.id} className="card p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-gray-500">ฐานคำนวณ: {BASIS_LABEL[p.basis] ?? p.basis} · {p.tiers.length} ขั้น</div>
                  {p.description && <div className="text-xs text-gray-600 mt-1">{p.description}</div>}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => setEditing({ ...p, tiers: p.tiers })} className="p-1 text-gray-400 hover:text-brand-600">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {p.active && (
                    <button onClick={async () => { if (await confirm({ title: 'ปิดใช้งานแพ็กเกจนี้?', danger: true, confirmText: 'ปิดใช้งาน' })) await remove.mutateAsync(p.id); }} className="p-1 text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-2 text-[11px] space-y-0.5">
                {p.tiers.sort((a, b) => a.tier_order - b.tier_order).map((t) => (
                  <div key={t.tier_order} className="flex justify-between bg-gray-50 rounded px-1.5 py-0.5">
                    <span>#{t.tier_order} {t.min_amount.toLocaleString()}–{t.max_amount?.toLocaleString() ?? '∞'}</span>
                    <span>{t.rate_percent ? `${t.rate_percent}%` : t.rate_fixed ? `${t.rate_fixed}/หน่วย` : '-'}</span>
                  </div>
                ))}
              </div>

              <div className="text-[10px] text-gray-500 mt-1 flex gap-2 flex-wrap">
                {p.min_threshold > 0 && <span>ขั้นต่ำ: {p.min_threshold.toLocaleString()}</span>}
                {p.cap !== null && <span>เพดาน: {Number(p.cap).toLocaleString()}</span>}
                {p.floor !== null && <span>รับประกัน: {Number(p.floor).toLocaleString()}</span>}
                {!p.active && <span className="text-gray-400">ปิดใช้งาน</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-semibold">{editing.id ? 'แก้ไข' : 'เพิ่ม'}แพคเกจค่าคอมมิชชั่น</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label">ชื่อแพคเกจ *</label>
                <input className="input" value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="label">คำอธิบาย</label>
                <input className="input" value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="label">ฐานคำนวณ</label>
                  <select className="input" value={editing.basis ?? 'gross_sales'} onChange={(e) => setEditing({ ...editing, basis: e.target.value as CommissionPackageRow['basis'] })}>
                    {COMMISSION_BASES.map((b) => <option key={b} value={b}>{BASIS_LABEL[b] ?? b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">ยอดขั้นต่ำที่เริ่มจ่าย</label>
                  <input type="number" className="input" value={editing.min_threshold ?? 0} onChange={(e) => setEditing({ ...editing, min_threshold: Number(e.target.value) })} />
                </div>
                <div>
                  <label className="label">เพดานสูงสุด</label>
                  <input type="number" className="input" value={editing.cap ?? ''} onChange={(e) => setEditing({ ...editing, cap: e.target.value === '' ? null : Number(e.target.value) })} />
                </div>
              </div>
              <div>
                <label className="label">ขั้นต่ำรับประกัน (จ่ายแน่นอน)</label>
                <input type="number" className="input" value={editing.floor ?? ''} onChange={(e) => setEditing({ ...editing, floor: e.target.value === '' ? null : Number(e.target.value) })} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label !mb-0">อัตราขั้นบันได</label>
                  <button onClick={addTier} type="button" className="text-xs text-brand-600">+ เพิ่มขั้น</button>
                </div>
                <div className="space-y-2">
                  {(editing.tiers ?? []).map((t, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1 items-center">
                      <span className="col-span-1 text-xs text-gray-500">#{t.tier_order}</span>
                      <input type="number" placeholder="ตั้งแต่" className="input col-span-3 text-xs px-1 py-1"
                        value={t.min_amount ?? 0}
                        onChange={(e) => {
                          const tiers = [...(editing.tiers ?? [])];
                          tiers[i] = { ...tiers[i], min_amount: Number(e.target.value) };
                          setEditing({ ...editing, tiers });
                        }}
                      />
                      <input type="number" placeholder="ถึง (ว่าง=ไม่จำกัด)" className="input col-span-3 text-xs px-1 py-1"
                        value={t.max_amount ?? ''}
                        onChange={(e) => {
                          const tiers = [...(editing.tiers ?? [])];
                          tiers[i] = { ...tiers[i], max_amount: e.target.value === '' ? null : Number(e.target.value) };
                          setEditing({ ...editing, tiers });
                        }}
                      />
                      <input type="number" placeholder="อัตรา %" className="input col-span-3 text-xs px-1 py-1"
                        value={t.rate_percent ?? ''}
                        onChange={(e) => {
                          const tiers = [...(editing.tiers ?? [])];
                          tiers[i] = { ...tiers[i], rate_percent: e.target.value === '' ? null : Number(e.target.value) };
                          setEditing({ ...editing, tiers });
                        }}
                      />
                      <button onClick={() => removeTier(i)} className="col-span-2 text-red-500 text-xs">ลบ</button>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={handleSave} disabled={upsert.isPending} className="btn-primary w-full">
                <Save className="w-4 h-4" /> {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Standalone page kept so legacy /hr/commission-packages still resolves
 *  (it now redirects via App.tsx, but the export is also reused by tests). */
export function HRCommissionPackages() {
  return (
    <AdminLayout title="แพคเกจค่าคอมมิชชั่น">
      <HRCommissionPackagesContent />
    </AdminLayout>
  );
}
